import { useNuxtApp } from '#imports'
import type { FirebaseApp } from 'firebase/app'
import type { Auth } from 'firebase/auth'
import type {
    CollectionReference,
    DocumentData,
    DocumentReference,
    DocumentSnapshot,
    FieldValue,
    Firestore,
    Query
} from 'firebase/firestore'
import type { Functions } from 'firebase/functions'
import type { FirebaseStorage } from 'firebase/storage'

type FirebaseCollections = {
    jobRequests: CollectionReference
    jobs: CollectionReference
    mentorings: CollectionReference
    notifications: CollectionReference
    orgs: CollectionReference
    orgTransactions: CollectionReference
    communities: CollectionReference
    communityConnectionRequests: CollectionReference
    profiles: CollectionReference
    projects: CollectionReference
    skills: CollectionReference
    users: CollectionReference
    talentBaseRequests: CollectionReference
    participationRequests: CollectionReference
    blogs: CollectionReference
    intros: CollectionReference
    settings: CollectionReference
    chats: CollectionReference
    chatMessages: (chatId: string) => CollectionReference
    paidServiceRequests: CollectionReference
    agreements: CollectionReference
    consents: CollectionReference
    membershipRequests: CollectionReference
    collections: CollectionReference
    selfKnowledgeSurveys: CollectionReference
    feedbacks: CollectionReference
}

type FirebaseClient = {
    app: FirebaseApp
    db: Firestore
    fn: Functions
    storage: FirebaseStorage
    collections: FirebaseCollections
    /** Loads Firestore on demand; `db` and `collections` throw until it has resolved. */
    ensureFirestore(): Promise<Firestore>
    getCurrentUserId(): string | undefined
    getServerTimestamp(): FieldValue
    getCollection(path: string): CollectionReference
    resolveCollection(collection: string | CollectionReference): CollectionReference
    serializeDocument<T extends DocumentData & { id: string }>(doc: DocumentSnapshot): T | null
    loadFirstDocument<T extends DocumentData & { id: string }>(
        collection: string | CollectionReference | Query,
        queryBuilder?: (query: CollectionReference) => Query
    ): Promise<T>
    loadFirstDocumentByAttr<T extends DocumentData & { id: string }>(
        collection: string | CollectionReference,
        attribute: string,
        value: unknown
    ): Promise<T>
    loadDocumentByAttrOrId<T extends DocumentData & { id: string }>(
        collection: string | CollectionReference,
        attribute: keyof T,
        value: unknown
    ): Promise<T>
    loadDocument<T extends DocumentData & { id: string }>(
        collection: string | CollectionReference,
        id: string
    ): Promise<T>
    loadDocumentOrFail<T extends DocumentData & { id: string }>(collection: CollectionReference, id: string): Promise<T>
    listDocuments<T extends DocumentData & { id: string }>(
        collection: string | CollectionReference | Query,
        queryBuilder?: (query: CollectionReference) => Query
    ): Promise<T[]>
    createDocument<T extends DocumentData & { id: string }>(
        collection: string | CollectionReference,
        data: Partial<T>,
        options?: { withAuthor?: boolean; withTimestamps?: boolean; withSoftDelete?: boolean }
    ): Promise<T>
    updateDocument<T extends DocumentData & { id: string }>(
        collection: string | CollectionReference,
        id: string,
        data: Partial<T>,
        options?: { withAuthor?: boolean; withTimestamps?: boolean; withSoftDelete?: boolean; useSet?: boolean }
    ): Promise<void>
    suspenseDocumentRef(ref: DocumentReference): undefined
    getCollectionByChatTopicBase(topicBase: string): string
}

type FirebaseAuth = Auth
type FirebaseContext = {
    $firebase: FirebaseClient
    auth: FirebaseAuth
    firestore: FirebaseClient['db']
    storage: FirebaseClient['storage']
    functions: FirebaseClient['fn']
    getCurrentUser: () => FirebaseAuth['currentUser']
    collections: FirebaseClient['collections']
}

export function useFirebase() {
    const ensureClientFirebase = (context: string = 'Firebase access'): FirebaseContext => {
        if (import.meta.server) {
            throw new Error(
                `${context} requires client-side Firebase. Move the load to onMounted() or useAsyncData(..., { server: false }).`
            )
        }

        const nuxtApp = useNuxtApp() as { $firebase?: FirebaseClient; $auth?: FirebaseAuth }
        const $firebase = nuxtApp.$firebase
        const $auth = nuxtApp.$auth

        if (!$firebase || !$auth) {
            throw new Error(`${context} requires the Firebase client plugin to be initialized.`)
        }

        // Firestore is loaded on demand, so neither `firestore` nor `collections` may be read here:
        // composables destructure this object during setup. `firestore` is a getter and
        // `collections` resolves each reference on access; both throw if Firestore isn't loaded yet.
        const collections = new Proxy({} as FirebaseCollections, {
            get: (_target, name) => Reflect.get($firebase.collections, name)
        })

        return {
            $firebase,
            auth: $auth,
            get firestore() {
                return $firebase.db
            },
            storage: $firebase.storage,
            functions: $firebase.fn,
            getCurrentUser: () => $auth.currentUser,
            collections
        }
    }

    if (import.meta.server) {
        return {
            $firebase: null as unknown as FirebaseClient,
            auth: null as unknown as FirebaseAuth,
            firestore: null as unknown as FirebaseClient['db'],
            storage: null as unknown as FirebaseClient['storage'],
            functions: null as unknown as FirebaseClient['fn'],
            getCurrentUser: () => null,
            collections: null as unknown as FirebaseClient['collections'],
            ensureClientFirebase
        }
    }

    // Object.assign keeps the lazy getters; a spread would read them immediately.
    return Object.assign(ensureClientFirebase(), { ensureClientFirebase })
}
